/**
 * Canonicalize a MathLive LaTeX value before it is stored or graded (PBI 535629).
 *
 * The bug this closes: an author's correct answer was stored as `-3_{_{}}` — an empty subscript
 * nested in an empty subscript, left behind by copying `-3` out of another field. KaTeX renders it
 * as a plain `-3`, MathLive's own validator reports nothing, but the Compute Engine cannot attach a
 * subscript to a number and parses an Error, so the learner's `-3` could never match. The same
 * shape recurs with every construct that is invisible once rendered yet visible to the engine:
 *
 *  - Unicode format characters (zero-width spaces and joiners, bidirectional marks, BOM, soft
 *    hyphen) that ride along with text copied from Word or Google Docs — the engine reads them as
 *    tokens (`3x\u200B+2` is an Error);
 *  - Unicode spaces other than U+0020 — a thin or narrow space mid-expression is an Error, and a
 *    non-breaking one between digits joins them;
 *  - LaTeX spacing commands mid-expression — the engine reads `3x\,+2` as `3x·(+2)`, a juxtaposed
 *    product, so the value silently becomes `6x`;
 *  - empty wrappers (`\text{}`, `\mathrm{}`, `\placeholder{}`) and empty script branches (`_{}`,
 *    `^{}`), which MathLive's serializer emits deliberately for an empty branch.
 *
 * Only invisible material is touched: visible content passes through verbatim, so an author's `\sin`,
 * `\left(`, `\operatorname{…}` or a filled subscript is never rewritten. `\,` between two digits stays,
 * because the engine reads `3\,000` correctly as a thousands-grouped number. Removing something that
 * stood between a control word and a letter leaves a space in its place — `\pi\,r` becomes `\pi r`,
 * never `\pir`, which is an unknown command. Single letters may touch: the engine reads `xy`, `x\,y`
 * and `x y` as the same product. The result is whitespace-collapsed and trimmed, so two values that
 * render identically compare identically.
 *
 * Pure and dependency-free: it runs in the field wrapper (both Studio and Player) and in grading (both
 * sides, browser and API). Character classes are enumerated as explicit ranges — the project targets
 * es2015 and TypeScript 5.5 rejects `\p{Cf}` (es2018).
 */

// Format characters (Unicode category Cf) plus the soft hyphen (Cf as well) — invisible when rendered.
const INVISIBLE_CHARS = /[\u00AD\u061C\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;

// Every Unicode space separator (category Zs) other than U+0020, mapped to a plain space.
const OTHER_SPACES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;

// Spacing commands that take no argument. `\ ` (control space) and the `~` tie are mapped to a
// plain space rather than dropped, since they may separate two letter runs.
const BARE_SPACING_COMMANDS = new Set([
  ',',
  ':',
  ';',
  '!',
  '>',
  'quad',
  'qquad',
  'enspace',
  'thinspace',
  'medspace',
  'thickspace',
  'negthinspace',
  'negmedspace',
  'negthickspace',
  'strut',
  'mathstrut'
]);

// Spacing commands whose single brace argument is dropped along with them.
const ARGUMENT_SPACING_COMMANDS = new Set(['hspace', 'hspace*', 'mspace', 'phantom', 'hphantom', 'vphantom']);

// Kerning commands followed by a bare dimension (`\mkern3mu`, `\kern-0.5em`).
const DIMENSION_SPACING_COMMANDS = new Set(['mkern', 'kern']);

// Wrappers that are dropped only when their argument is empty (or whitespace); a filled one is kept.
const EMPTY_WRAPPER_COMMANDS = new Set([
  'placeholder',
  'text',
  'textrm',
  'textit',
  'textbf',
  'textsf',
  'texttt',
  'mathrm',
  'mathit',
  'mathbf',
  'mathsf',
  'mathtt',
  'mathcal',
  'mathbb',
  'mathfrak',
  'mathscr',
  'operatorname',
  'underline',
  'overline'
]);

// `_{}` / `^{}` whose `_`/`^` is not itself escaped (`\_` is a literal underscore). The leading
// "start-or-non-backslash" group stands in for a lookbehind, which the es2015 target cannot ship.
const EMPTY_SCRIPT = /(^|[^\\])[_^]\{\s*\}/g;

// A control word (`\pi`, `\sin`, `\hspace*`) ending the text kept so far.
const TRAILING_CONTROL_WORD = /\\[a-zA-Z]+\*?$/;

const DIMENSION = /^\s*-?(?:\d+\.?\d*|\.\d+)\s*(?:mu|em|ex|pt|px|cm|mm|in)?/;

const isDigit = (char: string | undefined): boolean => char !== undefined && char >= '0' && char <= '9';
const isLetter = (char: string | undefined): boolean =>
  char !== undefined && ((char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z'));

/**
 * What stands in for removed material between `before` and the character `next`: a space when a
 * control word would otherwise fuse with a following letter into a different command, else nothing.
 */
const separator = (before: string, next: string | undefined): string =>
  TRAILING_CONTROL_WORD.test(before) && isLetter(next) ? ' ' : '';

/** The command name starting at `start` (which holds the backslash) and the index just past it. */
function readCommand(source: string, start: number): { name: string; end: number } {
  let end = start + 1;
  if (isLetter(source[end])) {
    while (isLetter(source[end])) end++;
    if (source[end] === '*') end++;
  } else if (end < source.length) {
    end++;
  }
  return { name: source.slice(start + 1, end), end };
}

/**
 * The brace group starting at `start` (after optional whitespace and an optional `[…]` argument),
 * or null when the command has no brace argument there.
 */
function readBraceGroup(source: string, start: number): { inner: string; end: number } | null {
  let index = start;
  while (source[index] === ' ') index++;
  if (source[index] === '[') {
    const close = source.indexOf(']', index);
    if (close === -1) return null;
    index = close + 1;
    while (source[index] === ' ') index++;
  }
  if (source[index] !== '{') return null;

  let depth = 0;
  for (let cursor = index; cursor < source.length; cursor++) {
    const char = source[cursor];
    if (char === '\\') {
      cursor++;
      continue;
    }
    if (char === '{') depth++;
    else if (char === '}') {
      depth--;
      if (depth === 0) return { inner: source.slice(index + 1, cursor), end: cursor + 1 };
    }
  }
  return null;
}

export type NormalizeMathLatexOptions = {
  // A slot expression's student slot is an EMPTY prompt, `\placeholder[<id>]{}` (PBI 538086) — the one
  // empty wrapper that is content. Kept only where an expression is edited; a graded value never has one.
  keepPrompts?: boolean;
};

// Whether the command ending at `end` is followed by an optional `[…]` argument — a prompt's id.
const hasOptionalArgument = (source: string, end: number): boolean => /^\s*\[/.test(source.slice(end));

function normalizeOnce(latex: string, options: NormalizeMathLatexOptions): string {
  const source = latex.replace(INVISIBLE_CHARS, '').replace(OTHER_SPACES, ' ');
  let out = '';
  let index = 0;

  // Skip the source up to `dropEnd`, keeping whatever surrounded the dropped material apart.
  const drop = (dropEnd: number) => {
    out += separator(out, source[dropEnd]);
    index = dropEnd;
  };

  while (index < source.length) {
    const char = source[index];
    if (char === '~') {
      out += ' ';
      index++;
      continue;
    }
    if (char !== '\\') {
      out += char;
      index++;
      continue;
    }

    const { name, end } = readCommand(source, index);

    if (name === ' ') {
      out += ' ';
      index = end;
      continue;
    }
    if (BARE_SPACING_COMMANDS.has(name)) {
      // A thin space between digits is a thousands separator the engine reads correctly; keep it.
      if (name === ',' && isDigit(out[out.length - 1]) && isDigit(source[end])) {
        out += '\\,';
        index = end;
      } else {
        drop(end);
      }
      continue;
    }
    if (ARGUMENT_SPACING_COMMANDS.has(name)) {
      drop(readBraceGroup(source, end)?.end ?? end);
      continue;
    }
    if (DIMENSION_SPACING_COMMANDS.has(name)) {
      const dimension = DIMENSION.exec(source.slice(end));
      drop(end + (dimension?.[0].length ?? 0));
      continue;
    }
    const isKeptPrompt = options.keepPrompts && name === 'placeholder' && hasOptionalArgument(source, end);
    if (EMPTY_WRAPPER_COMMANDS.has(name) && !isKeptPrompt) {
      const group = readBraceGroup(source, end);
      if (group && group.inner.trim() === '') {
        drop(group.end);
        continue;
      }
    }

    out += source.slice(index, end);
    index = end;
  }

  out = out.replace(EMPTY_SCRIPT, (match: string, prefix: string, offset: number, whole: string) => {
    return prefix + separator(whole.slice(0, offset + prefix.length), whole[offset + match.length]);
  });

  return out.replace(/\s+/g, ' ').trim();
}

export function normalizeMathLatex(latex: string, options: NormalizeMathLatexOptions = {}): string {
  // Repeated until stable: `_{_{}}` collapses through `_{}` to nothing, and a wrapper whose content
  // was only invisible material (`\text{\,}`) is empty only once its inside has gone.
  let out = latex;
  let previous: string;
  do {
    previous = out;
    out = normalizeOnce(out, options);
  } while (out !== previous);
  return out;
}
